// Service planning: who can do the repair, where the parts and tools are, when the weather allows it,
// and what each option costs, including the risk of failure while waiting.
import { clamp } from './util.js';
import { TURB, LOG, FAULTS } from './content.js';
import { SIM, FARM } from './fleet.js';
import { WX, H, hourDate } from './weather.js';
import { failureOdds } from './health.js';
import { faultLossShare } from './physics.js';
import { route, initRoads } from './routing.js';

var R = function () { return LOG.rates; };
export var HUB = {};
export function initLogistics() { HUB = {}; LOG.hubs.forEach(function (h) { HUB[h.id] = h; }); initRoads(); }

// drive from a place to a farm on the road network: km, hours one way (with handover) and there and back
export function trip(from, to) { var r = route(from, to); return { km: r.km, h: r.h, back: r.back, route: r }; }
export function where(crew) { if (crew.at && FARM[crew.at]) return FARM[crew.at]; return HUB[crew.hub]; }

// can the job be worked at hour i on this farm?
export function workable(job, farm, i) {
  var hod = hourDate(i).getHours(); if (hod < R().shiftStart || hod >= R().shiftEnd) return { ok: false, why: 'night' };
  if (i >= H) return { ok: true, why: 'beyond forecast' };
  var w = WX[farm.id].hours[i], lim = job.windLimit;
  if (w.v > lim) return { ok: false, why: 'wind' };
  if (w.g > lim * 1.6) return { ok: false, why: 'gusts' };
  if (job.rhMax && w.rh > job.rhMax) return { ok: false, why: 'humidity' };
  if (job.tempMin != null && w.t < job.tempMin) return { ok: false, why: 'cold' };
  if (w.ice > 0.5 && job.skill === 'rope') return { ok: false, why: 'ice' };
  if (w.cond === 'thunderstorm') return { ok: false, why: 'thunderstorm' };
  return { ok: true, why: '' };
}

// earliest start at or after t0 and the hours the turbine is down until the work is done
function schedule(job, farm, t0, limit) {
  for (var s = Math.ceil(t0); s < limit; s++) {
    if (!workable(job, farm, s).ok) continue;
    var done = 0, h = s;
    while (done < job.hours && h < limit + 96) { if (workable(job, farm, h).ok) done++; h++; }
    if (done >= job.hours) return { start: s, end: h };
  }
  return null;
}

function bestSource(item, qty, farm, crew, isTool) {
  var opts = [];
  LOG.hubs.forEach(function (hub) {
    var have = isTool ? hub.tools.indexOf(item) >= 0 : (hub.stock[item] || 0) >= qty;
    if (!have) return;
    var tr = trip(hub, farm), withCrew = crew && crew.hub === hub.id && !crew.at;
    opts.push({ hub: hub, km: tr.km, route: tr.route, eta: withCrew ? null : 1 + tr.h, withCrew: withCrew, stock: isTool ? null : hub.stock[item] });
  });
  if (!opts.length && !isTool) { var f = LOG.factory.leadDays[item] || 14; return { hub: null, factory: true, eta: f * 24, km: 0 }; }
  opts.sort(function (a, b) { return (a.withCrew ? -1 : 0) - (b.withCrew ? -1 : 0) || a.eta - b.eta; });
  return opts[0] || null;
}

function avgPrice(i) { return i < H ? Math.max(0, SIM.price[i]) : LOG.rates.pricePerMWhFallback; }
// capacity factor of the farm's site class (full-load hours a year: coast 3,200, north 2,500, low mountains 1,800; Fraunhofer ISE 2024)
export function cf(farm) { return (farm.flh || 2500) / 8760; }
function expectedKW(t, i) { return i < H ? SIM.tur[t.id].av[i] : cf(t.farm) * TURB.ratedKW; }

// cost of a failure: parts and repair, crane, and the energy lost while the turbine waits (illustrative)
export function failureCost(f, farm) {
  var cr = FAULTS.cranes[f.failure.crane] || { cost: 0 }, lost = f.failure.downtimeDays * 24 * cf(farm) * TURB.ratedKW / 1000 * LOG.rates.pricePerMWhFallback;
  return { parts: f.failure.cost, crane: cr.cost, lost: lost, total: f.failure.cost + cr.cost + lost, days: f.failure.downtimeDays };
}

// all plan options for an issue, cheapest expected cost first
export function planOptions(t, derate) {
  var is = t.issue; if (!is) return null;
  var f = is.f, job = f.repair, farm = t.farm, odds = failureOdds(t, { derate: derate }), fc = failureCost(f, farm), crews = [];
  if (job.remote) crews.push(remoteOption(t, job, odds, fc));
  LOG.crews.forEach(function (c) {
    if (job.remote || c.skills.indexOf(job.skill) < 0) return;
    var from = where(c), tr = trip(from, farm), ready = Math.max(c.freeAt, 0) + tr.h;
    var parts = job.parts.filter(function (p) { return p.qty > 0; }).map(function (p) { return Object.assign({ id: p.id, qty: p.qty, name: LOG.parts[p.id] }, bestSource(p.id, p.qty, farm, c, false)); });
    var tools = job.tools.map(function (id) { return Object.assign({ id: id, name: LOG.tools[id] }, bestSource(id, 1, farm, c, true) || { missing: true }); });
    var crane = null, craneCost = (FAULTS.cranes[job.crane] || { cost: 0 }).cost;
    if (job.crane === 'mobile130' || job.crane === 'crawler750') {
      var cdef = LOG.cranes.filter(function (k) { return k.type === job.crane; })[0], ct = trip(Object.assign({ id: 'crane-' + cdef.type }, cdef.base), farm);
      crane = { name: cdef.name, eta: cdef.freeAt + cdef.mobiliseHours + ct.h, windLimit: cdef.windLimit, cost: craneCost };
    }
    var partsReady = parts.reduce(function (a, p) { return Math.max(a, p.eta == null ? ready : p.eta); }, 0);
    var toolsReady = tools.reduce(function (a, p) { return Math.max(a, p.missing ? 1e9 : p.eta == null ? ready : p.eta); }, 0);
    var t0 = Math.max(ready, partsReady, toolsReady, crane ? crane.eta : 0);
    var jobX = Object.assign({}, job, { windLimit: crane ? Math.min(job.windLimit, crane.windLimit) : job.windLimit });
    var sch = schedule(jobX, farm, t0, Math.max(H, t0) + 24 * 10);
    if (!sch) return;
    var down = sch.end - sch.start, workers = Math.max(job.crew, Math.min(c.size, job.crew + 1));
    var lostMWh = 0, lostEur = 0;
    for (var i = sch.start; i < sch.end; i++) { var kw = expectedKW(t, i); lostMWh += kw / 1000; lostEur += kw / 1000 * avgPrice(i); }
    var seen = {}, courierKm = parts.concat(tools).reduce(function (a, p) { if (p.eta == null || p.factory || !p.hub || seen[p.hub.id]) return a; seen[p.hub.id] = 1; return a + p.km; }, 0);
    var cost = { parts: job.partsCost, labour: workers * (job.hours + tr.back) * LOG.rates.techPerHour, vehicle: 2 * tr.km * (LOG.rates.vanPerKm || 0), crane: crane ? craneCost : 0, courier: courierKm * LOG.rates.courierPerKm, extra: job.extraCost, lostEnergy: lostEur };
    var direct = cost.parts + cost.labour + cost.vehicle + cost.crane + cost.courier + cost.extra + cost.lostEnergy;
    var pFailFirst = odds ? odds.pBefore(sch.start) : 0, risk = pFailFirst * fc.total;
    crews.push({ crew: c, from: from, travel: tr, ready: ready, parts: parts, tools: tools, crane: crane, t0: t0, start: sch.start, end: sch.end, down: down, lostMWh: lostMWh,
      cost: cost, direct: direct, pFailFirst: pFailFirst, risk: risk, expected: direct + risk });
  });
  crews.sort(function (a, b) { return a.expected - b.expected; });
  // doing nothing over the next 3 months: chance of failure × cost of the failure, plus the yield the fault keeps costing
  var runToFail = odds ? { p: odds.p90d, expected: odds.p90d * fc.total + yieldLoss(t, 90), median: odds.median } : { p: 0, expected: yieldLoss(t, 90) };
  return { issue: is, fault: f, job: job, odds: odds, failure: fc, options: crews, best: crews[0] || null, runToFail: runToFail };
}

// a fix the remote service centre makes over the turbine's data link: no travel, starts in the next hour
function remoteOption(t, job, odds, fc) {
  var start = 1, end = start + job.hours, lostMWh = 0, lostEur = 0;
  for (var i = start; i < end; i++) { var kw = expectedKW(t, i) * 0.3; lostMWh += kw / 1000; lostEur += kw / 1000 * avgPrice(i); }   // test runs at reduced power
  var cost = { parts: job.partsCost, labour: 0, vehicle: 0, crane: 0, courier: 0, extra: job.extraCost, lostEnergy: lostEur }, direct = cost.parts + cost.extra + cost.lostEnergy;
  return { crew: { id: 'RC', name: 'Remote service centre', size: 1, skills: [], hub: null, freeAt: 0, doing: 'on duty', remote: true }, from: { name: 'Remote', city: 'Remote' }, remote: true,
    travel: { km: 0, h: 0, back: 0, route: null }, ready: start, parts: [], tools: [], crane: null, t0: start, start: start, end: end, down: job.hours, lostMWh: lostMWh,
    cost: cost, direct: direct, pFailFirst: 0, risk: 0, expected: direct };
}

// yield lost by an unfixed aerodynamic issue over `days` (EUR)
export function yieldLoss(t, days) { return faultLossShare(t.issue) * cf(t.farm) * TURB.ratedKW / 1000 * 24 * days * LOG.rates.pricePerMWhFallback; }
export function annualYieldLoss(t) { return yieldLoss(t, 365); }

export function crewStatus(c) {
  if (c.remote) return { label: 'Remote service centre, on duty', free: true };
  if (c.freeAt <= 0) return { label: 'Free now', free: true };
  return { label: 'Busy for ' + Math.round(c.freeAt) + ' h · ' + c.doing, free: false };
}
export function clampHour(h) { return clamp(h, 0, H - 1); }
